import { useEffect, useMemo, useState } from "react";
import { Loader2, Search, ShieldCheck, UserRound, X } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { sourceLabel } from "@/lib/access";

const SRC_STYLE = {
  audience: "bg-slate-100 text-slate-600 border-slate-200",
  role: "bg-[#eef2fb] text-primary border-primary/20",
  user: "bg-amber-50 text-amber-700 border-amber-200",
  super_admin: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const kindOf = (src) => (src?.startsWith("audience:") ? "audience" : src);

/** Every effective permission with the layer it came from, plus what was
 *  taken away and by which layer. `data` = GET /users/{id}/effective-permissions. */
export function ExplainAccessPanel({ data, catalog, compact = false }) {
  const audLabels = useMemo(() => Object.fromEntries((data?.audiences || []).map((a) => [a.audience_id, a.label])), [data]);
  const labelOf = useMemo(() => {
    const m = {};
    (catalog?.modules || []).forEach((x) => { m[x.id] = x.label; });
    return m;
  }, [catalog]);
  if (!data) return null;
  const eff = data.effective || [];
  const grouped = eff.reduce((acc, p) => {
    const mod = p.split(".")[0];
    (acc[mod] = acc[mod] || []).push(p);
    return acc;
  }, {});
  const revoked = Object.entries(data.revoked || {});
  const counts = {
    audience: eff.filter((p) => (data.sources[p] || []).some((s) => s.startsWith("audience:"))).length,
  };
  return (
    <div className="space-y-3" data-testid="explain-access-panel">
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="rounded-md border border-slate-200 bg-white px-2 py-0.5 font-medium text-slate-700" data-testid="explain-role">{data.role_label || data.role}</span>
        {data.partner_type && <span className="rounded-md border border-slate-200 bg-white px-2 py-0.5 text-slate-500" data-testid="explain-partner-type">{data.partner_type}</span>}
        <span className="text-slate-400">in</span>
        {(data.audiences || []).length === 0 && <span className="text-slate-400">no audience</span>}
        {(data.audiences || []).map((a) => (
          <span key={a.audience_id} className={`rounded-md border px-2 py-0.5 ${SRC_STYLE.audience}`} data-testid={`explain-audience-${a.audience_id}`}>{a.label}</span>
        ))}
        <span className="ml-auto font-medium text-slate-600" data-testid="explain-total">{eff.length} effective</span>
      </div>
      <div className="flex flex-wrap gap-3 text-[11px] text-slate-500" data-testid="explain-summary">
        <span>{counts.audience} from audiences</span>
        <span>Role: +{(data.role_grants || []).length} / −{(data.role_revoked || []).length}</span>
        <span>User: +{(data.user_grants || []).length} / −{(data.user_revoked || []).length}</span>
        <span className="ml-auto flex items-center gap-2">
          {Object.entries(SRC_STYLE).filter(([k]) => k !== "super_admin").map(([k, cls]) => (
            <span key={k} className={`rounded border px-1.5 py-0.5 capitalize ${cls}`}>{k}</span>
          ))}
        </span>
      </div>
      {revoked.length > 0 && (
        <div className="rounded-lg border border-rose-200 bg-rose-50/60 px-3 py-2" data-testid="explain-revoked">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-rose-700">Taken away</div>
          <div className="flex flex-wrap gap-1.5">
            {revoked.map(([p, by]) => (
              <span key={p} className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-2 py-0.5 text-xs text-rose-700 line-through decoration-rose-400" title={`Revoked by ${by.map((s) => sourceLabel(s)).join(", ")}`} data-testid={`explain-revoked-${p}`}>
                <X className="h-3 w-3" /> {p} <span className="text-[10px] no-underline text-rose-500">by {by.map((s) => sourceLabel(s)).join(", ")}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      <div className={`${compact ? "max-h-[50vh]" : "max-h-[60vh]"} space-y-3 overflow-y-auto pr-1`} data-testid="explain-list">
        {eff.length === 0 && <div className="py-6 text-center text-sm text-slate-400">No permissions at all.</div>}
        {Object.entries(grouped).sort(([a], [b]) => (labelOf[a] || a).localeCompare(labelOf[b] || b)).map(([mod, perms]) => (
          <div key={mod}>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{labelOf[mod] || mod.replace(/_/g, " ")}</div>
            <div className="flex flex-wrap gap-1.5">
              {perms.map((p) => {
                const srcs = data.sources?.[p] || [];
                const kind = kindOf(srcs.find((s) => s === "user") || srcs.find((s) => s === "role") || srcs[0] || "audience:");
                return (
                  <span key={p} data-testid={`eff-perm-${p}`} data-source={kind}
                    title={`From: ${srcs.map((s) => sourceLabel(s, audLabels)).join(", ")}`}
                    className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium ${SRC_STYLE[kind] || SRC_STYLE.audience}`}>
                    {p.split(".")[1]}
                    <span className="text-[10px] font-normal opacity-70">{srcs.map((s) => sourceLabel(s, audLabels)).join(" · ")}</span>
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function useExplainAccess(userId, open = true) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !userId) { setData(null); return; }
    setLoading(true); setData(null);
    api.get(`/users/${userId}/effective-permissions`)
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [userId, open]);
  return { data, loading };
}

/** Permissions → Explain tab: pick any person, see why they can (or can't). */
export default function ExplainAccessTab({ catalog }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const { data, loading } = useExplainAccess(picked?.user_id);

  useEffect(() => {
    const needle = q.trim();
    if (needle.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/users?search=${encodeURIComponent(needle)}`)
        .then((r) => setResults((r.data || []).slice(0, 12))).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <Card className="border-slate-200 p-4 shadow-sm" data-testid="explain-card">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a person by name or email…"
            data-testid="explain-user-search" className="w-72 pl-8" />
        </div>
        {picked && (
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm" data-testid="explain-picked">
            <UserRound className="h-4 w-4 text-slate-400" /> {picked.name} <span className="text-xs text-slate-400">{picked.email}</span>
            <button type="button" onClick={() => setPicked(null)} className="text-slate-400 hover:text-slate-700" data-testid="explain-clear"><X className="h-3.5 w-3.5" /></button>
          </span>
        )}
      </div>
      {results.length > 0 && !picked && (
        <ul className="mt-2 max-h-64 divide-y divide-slate-100 overflow-auto rounded-lg border border-slate-200" data-testid="explain-results">
          {results.map((u) => (
            <li key={u.user_id}>
              <button type="button" onClick={() => { setPicked(u); setResults([]); }} data-testid={`explain-result-${u.user_id}`}
                className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50">
                <span className="font-medium text-slate-800">{u.name}</span>
                <span className="text-xs text-slate-400">{u.email}</span>
                <span className="ml-auto text-xs capitalize text-slate-500">{u.role_label || u.role}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-4">
        {!picked ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-slate-400">
            <ShieldCheck className="h-8 w-8 text-slate-200" />
            Pick a person to see every permission they hold, which layer gave it, and what was taken away.
          </div>
        ) : loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
        ) : !data ? (
          <div className="py-10 text-center text-sm text-slate-400">Could not load this person&apos;s access.</div>
        ) : (
          <ExplainAccessPanel data={data} catalog={catalog} />
        )}
      </div>
    </Card>
  );
}
